import React, {
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import './styles.scss';
import Typography from 'Components/Atoms/Typography';
import IconButton from 'Components/Atoms/IconButton';
import { CheckCircleOutline, ChevronLeft, Close } from 'Components/Atoms/Icons';
import { RestaurantContext } from 'Contexts/RestaurantContext';
import { AuthContext } from 'Contexts/AuthContext';
import { useSupportTopic } from 'Contexts/SupportRequestContext';
import useLanguage from 'CustomHooks/useLanguage';
import useSupportApi from 'CustomHooks/Support/useSupportApi';
import {
  isConversationUnread,
  isOpenForRestaurant,
  useSupportConversations,
  useSupportTickets,
} from 'CustomHooks/Support/useSupportData';
import {
  SupportConversationTemplate,
  SupportMessageBlock,
} from 'types/shared/documents/support';
import { RestaurantTicketSummary } from 'types/shared/routes/v03/support';
import SupportOverview from './SupportOverview';
import ChatView from './ChatView';
import SupportComposer from './SupportComposer';
import TicketDetail from './TicketDetail';
import NotifyByEmail from './NotifyByEmail';
import { resolveTr } from './helpers';
import { captureScreenshot } from './screenshot';
import ChangelogView from './ChangelogView';

type SupportPanelProps = {
  /** 'panel' insets the dashboard (desktop); 'page' is a full-screen route (mobile). */
  variant?: 'panel' | 'page';
  /** Called when the user closes support (defaults to collapsing the panel). */
  onClose?: () => void;
};

const SupportPanel = ({ variant = 'panel', onClose }: SupportPanelProps) => {
  const { restaurantId, setSupportOpen, alert, restaurantEmail } =
    useContext(RestaurantContext);
  const { uid, user } = useContext(AuthContext);
  const { language } = useLanguage();
  const {
    request: topicRequest,
    consumeSupportTopic,
    changelogRequested,
    consumeSupportChangelog,
  } = useSupportTopic();
  const [requestSource, setRequestSource] = useState<string | null>(null);
  const {
    getTemplates,
    createConversation,
    postMessage,
    closeConversation,
    updateConversation,
    markConversationRead,
    rateMessage,
    uploadAttachment,
  } = useSupportApi();

  const { conversations, loading: conversationsLoading } =
    useSupportConversations(true);
  // Refetch the ticket list when the (live) conversations link new ticket ids,
  // so a ticket Mia creates mid-chat shows up without reopening the panel.
  // Sorted, so a new message that only reorders the list does not refetch.
  const ticketsKey = Array.from(
    new Set(
      ([] as string[]).concat(...conversations.map((c) => c.tickets || [])),
    ),
  )
    .sort()
    .join('.');
  // Waits for the conversations, or it would fetch twice: once without and
  // once with their ticket ids.
  const { tickets } = useSupportTickets(!conversationsLoading, ticketsKey);

  const [templates, setTemplates] = useState<SupportConversationTemplate[]>([]);
  // Without a uid getTemplates answers [] without asking, which is not "no
  // templates" yet.
  const [templatesLoaded, setTemplatesLoaded] = useState(false);
  // A failed load is not "no templates": a topic asked for keeps waiting and
  // the templates can be loaded again.
  const [templatesFailed, setTemplatesFailed] = useState(false);
  const [templatesAttempt, setTemplatesAttempt] = useState(0);
  // The request that was waiting when the load failed; a newer one loads again.
  const failedForRequestRef = useRef(0);
  const topicRequestRef = useRef(topicRequest);
  topicRequestRef.current = topicRequest;
  // A topic asked for here and not yet prepared (templates loading, failed or
  // retrying): nothing may be sent meanwhile, or it would go into the
  // conversation still on screen, or out without the topic.
  const topicPending =
    !!topicRequest && topicRequest.restaurantId === restaurantId;
  const topicPendingRef = useRef(topicPending);
  topicPendingRef.current = topicPending;
  const [activeId, setActiveId] = useState<string | null>(null);
  const [activeTicketId, setActiveTicketId] = useState<string | null>(null);
  // The ticket the open conversation was reached from; "back" returns there.
  const [returnTicketId, setReturnTicketId] = useState<string | null>(null);
  // A ticket without a conversation: the first message will open one about it.
  // A copy, so the question survives a refetch that drops the ticket.
  const [askSnapshot, setAskSnapshot] =
    useState<RestaurantTicketSummary | null>(null);
  // A template picked on the overview: the first message will open its
  // conversation, so a click alone leaves nothing in the inbox.
  const [pendingTemplate, setPendingTemplate] =
    useState<SupportConversationTemplate | null>(null);
  const [changelogOpen, setChangelogOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  // Lives here, not in SupportOverview: that component unmounts on every
  // ticket/chat and would collapse both lists again on the way back.
  const [ticketsExpanded, setTicketsExpanded] = useState(false);
  const [conversationsExpanded, setConversationsExpanded] = useState(false);
  const navigatedRef = useRef(false);
  // Bumped on a restaurant switch: a send that returns after it must not open
  // the previous restaurant's conversation.
  const restaurantSeqRef = useRef(0);
  const panelRef = useRef<HTMLElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);

  const activeTicket = activeTicketId
    ? tickets.find((t) => t.id === activeTicketId) || null
    : null;
  const askTicket = askSnapshot
    ? tickets.find((t) => t.id === askSnapshot.id) || askSnapshot
    : null;

  // An open ticket that left the list must not keep its id: it would swallow
  // the next back and reopen on its own once the ticket is listed again.
  useEffect(() => {
    if (activeTicketId && !activeTicket) setActiveTicketId(null);
  }, [activeTicketId, activeTicket]);

  // The panel variant insets the dashboard body; the page variant is its own
  // route and must not inset. Layout effect avoids a one-frame overlay flash.
  useLayoutEffect(() => {
    if (variant !== 'panel') return;
    document.body.classList.add('support-open');
    return () => document.body.classList.remove('support-open');
  }, [variant]);

  // The NavBar is as tall as its tallest control, which differs per page, so
  // the header copies that height instead of assuming one. Every screen brings
  // its own NavBar: one that left the DOM reports 0 once and then stays silent,
  // so the observer moves on to the next. The search is quick while a screen
  // mounts and slow for a chunk that takes its time.
  useLayoutEffect(() => {
    if (variant !== 'panel' || !window.ResizeObserver) return;
    let observer: ResizeObserver | undefined;
    let frame = 0;
    let timer = 0;
    let tries = 0;
    const setHeight = (height: number) => {
      const style = panelRef.current?.style;
      if (height) style?.setProperty('--support-header-height', `${height}px`);
      else style?.removeProperty('--support-header-height');
    };
    const attach = () => {
      const navbar = document.querySelector<HTMLElement>('.navbar');
      if (!navbar) {
        setHeight(0);
        if (tries++ < 120) frame = requestAnimationFrame(attach);
        else timer = window.setTimeout(attach, 500);
        return;
      }
      observer = new ResizeObserver(() => {
        if (!navbar.isConnected) {
          observer?.disconnect();
          tries = 0;
          attach();
          return;
        }
        setHeight(navbar.getBoundingClientRect().height);
      });
      observer.observe(navbar);
    };
    attach();
    return () => {
      cancelAnimationFrame(frame);
      window.clearTimeout(timer);
      observer?.disconnect();
    };
  }, [variant]);

  useEffect(() => {
    let active = true;
    setTemplatesFailed(false);
    getTemplates().then(
      (result) => {
        if (!active) return;
        setTemplates(result);
        if (uid) setTemplatesLoaded(true);
      },
      () => {
        if (!active) return;
        failedForRequestRef.current = topicRequestRef.current?.id ?? 0;
        setTemplatesFailed(true);
      },
    );
    return () => {
      active = false;
    };
  }, [getTemplates, uid, templatesAttempt]);

  const retryTemplates = () => setTemplatesAttempt((attempt) => attempt + 1);

  const openConversation = (
    conversationId: string,
    fromTicketId: string | null = null,
  ) => {
    navigatedRef.current = true;
    // First: after an await React 17 renders each setter on its own, and
    // clearing a draft before these are set would unmount the chat in between.
    setRequestSource(null);
    setActiveId(conversationId);
    setReturnTicketId(fromTicketId);
    setChangelogOpen(false);
    setActiveTicketId(null);
    setAskSnapshot(null);
    setPendingTemplate(null);
  };

  // The panel stays mounted across a restaurant switch — reset navigation so
  // the previous restaurant's conversation doesn't stay on screen.
  useEffect(() => {
    restaurantSeqRef.current += 1;
    setRequestSource(null);
    setActiveId(null);
    setActiveTicketId(null);
    setReturnTicketId(null);
    setAskSnapshot(null);
    setPendingTemplate(null);
    setChangelogOpen(false);
    navigatedRef.current = false;
  }, [restaurantId]);

  // A topic asked for elsewhere ("Abrechnungssupport") is prepared like a
  // template picked on the overview, once the templates are there. It counts
  // as navigation at once, so the last conversation does not open over it.
  useEffect(() => {
    if (!topicRequest) return;
    if (topicRequest.restaurantId !== restaurantId) {
      consumeSupportTopic(topicRequest.id);
      return;
    }
    navigatedRef.current = true;
    // Asked again after a failed load: load again (once per new request).
    if (templatesFailed && topicRequest.id > failedForRequestRef.current) {
      failedForRequestRef.current = topicRequest.id;
      retryTemplates();
      return;
    }
    // A send in flight opens its conversation when it returns; the topic
    // waits for that instead of being replaced by it.
    if (!templatesLoaded || busy) return;
    consumeSupportTopic(topicRequest.id);
    setRequestSource(topicRequest.source ?? null);
    setActiveId(null);
    setActiveTicketId(null);
    setReturnTicketId(null);
    setAskSnapshot(null);
    setChangelogOpen(false);
    // Unknown topic: the overview, where every template is offered.
    setPendingTemplate(
      templates.find((t) => t.id === topicRequest.templateId) || null,
    );
  }, [topicRequest, restaurantId, templatesLoaded, templatesFailed, templates, busy]);

  // "Neuigkeiten" asked for from the user menu opens on top of whatever was
  // on screen.
  useEffect(() => {
    if (!changelogRequested) return;
    consumeSupportChangelog();
    navigatedRef.current = true;
    setRequestSource(null);
    setActiveId(null);
    setActiveTicketId(null);
    setReturnTicketId(null);
    setAskSnapshot(null);
    setPendingTemplate(null);
    setChangelogOpen(true);
  }, [changelogRequested, consumeSupportChangelog]);

  // A request the panel closed before taking must not pop up next time.
  useEffect(
    () => () => {
      if (topicRequestRef.current)
        consumeSupportTopic(topicRequestRef.current.id);
    },
    [consumeSupportTopic],
  );

  // Open the most recent ongoing conversation on first load, unless the user
  // has already navigated.
  useEffect(() => {
    if (navigatedRef.current || activeId || activeTicketId) return;
    const ongoing = conversations.find(isOpenForRestaurant);
    if (ongoing) openConversation(ongoing.id);
  }, [conversations]);

  const openTicket = (ticketId: string) => {
    if (!tickets.some((t) => t.id === ticketId)) return;
    navigatedRef.current = true;
    setRequestSource(null);
    setChangelogOpen(false);
    setActiveTicketId(ticketId);
  };

  const back = () => {
    setRequestSource(null);
    navigatedRef.current = true;
    // Going back leaves a topic still waiting for its template.
    if (topicRequestRef.current) consumeSupportTopic(topicRequestRef.current.id);
    if (changelogOpen) {
      setChangelogOpen(false);
      return;
    }
    // A ticket opened from a chat returns to that chat; otherwise to overview.
    if (activeTicketId) {
      setActiveTicketId(null);
      return;
    }
    // A chat reached from a ticket returns to that ticket.
    const ticketId = askTicket?.id || returnTicketId;
    setAskSnapshot(null);
    setPendingTemplate(null);
    setReturnTicketId(null);
    setActiveId(null);
    if (tickets.some((t) => t.id === ticketId)) setActiveTicketId(ticketId);
  };

  const handleSubmit = async (text: string, files: File[] = []) => {
    // Covers the composer and the choices in a conversation alike.
    if (busy || topicPendingRef.current) return;
    const trimmed = text.trim();
    if (!trimmed && !files.length) return;
    const seq = restaurantSeqRef.current;
    const sameRestaurant = () => seq === restaurantSeqRef.current;
    setBusy(true);
    try {
      const content: SupportMessageBlock[] = [];
      if (trimmed) content.push({ type: 'text', text: trimmed });
      // Upload attachments in parallel; Promise.all preserves order.
      const uploaded = await Promise.all(
        files.map((file) => uploadAttachment(file)),
      );
      content.push(...uploaded);
      // Someone may have linked a conversation since the question was opened.
      const linked = askTicket
        ? conversations.find((c) => (c.tickets || []).includes(askTicket.id))
        : undefined;
      const target = activeId || linked?.id;
      if (target) {
        await postMessage(target, { content });
        if (!activeId && sameRestaurant())
          openConversation(target, askTicket?.id);
      } else {
        const initialMessage = { content: requestSource
          ? [{ type: 'text' as const, text: `Gestartet aus: ${requestSource}` }, ...content]
          : content };
        let ticketId = askTicket?.id;
        const conversation = await createConversation(
          askTicket
            ? { initialMessage, subject: askTicket.title, ticketId }
            : pendingTemplate
              ? {
                  initialMessage,
                  templateId: pendingTemplate.id,
                  subject: resolveTr(language, pendingTemplate.label),
                  group: pendingTemplate.group,
                  severity: pendingTemplate.severityDefault,
                }
              : { initialMessage },
        ).catch((error) => {
          // Ticket gone (404) or at its conversation cap (409): the question
          // still has to arrive, so it goes out unlinked.
          const status = error?.response?.status;
          if (!askTicket || (status !== 404 && status !== 409)) throw error;
          ticketId = undefined;
          return createConversation({
            initialMessage,
            subject: askTicket.title,
          });
        });
        if (sameRestaurant()) openConversation(conversation.id, ticketId);
      }
    } finally {
      setBusy(false);
    }
  };

  const startTemplate = (template: SupportConversationTemplate) => {
    setRequestSource(null);
    navigatedRef.current = true;
    // Closing a chat keeps the ticket it was reached from.
    setReturnTicketId(null);
    setPendingTemplate(template);
  };

  const handleClose = () => {
    if (!activeId || busy) return;
    const conversationId = activeId;
    alert({
      title: 'Gespräch abschließen?',
      severity: 'warning',
      submitLabel: 'Ja, abschließen',
      description:
        'Das Gespräch wandert zu „Geschlossen“ und du findest es dort ' +
        'jederzeit wieder. Schreibst du später erneut hinein, geht es ' +
        'automatisch weiter.',
      onSubmit: () => {
        setBusy(true);
        closeConversation(conversationId)
          // Only clear what was actually closed: the list stays usable during
          // the request, so the user may already have opened another chat.
          .then(() =>
            setActiveId((current) =>
              current === conversationId ? null : current,
            ),
          )
          // ignore — the conversation stays open and the user can retry
          .catch(() => undefined)
          .finally(() => setBusy(false));
      },
    });
  };

  const handleRate = (messageId: string, dir: 'up' | 'down' | null) => {
    if (!activeId) return;
    rateMessage(activeId, messageId, dir).catch(() => undefined);
  };

  const handleConnectHuman = () => {
    if (!activeId || busy || topicPendingRef.current) return;
    const conversationId = activeId;
    alert({
      title: 'Mit dem Support-Team verbinden?',
      severity: 'warning',
      submitLabel: 'Mit Support verbinden',
      description:
        'Sobald du bestätigst, übernimmt unser Support-Team und die KI ' +
        'antwortet nicht mehr in dieser Unterhaltung. Wir sind Mo–Fr von ' +
        '10 bis 20 Uhr für dich da. Je nach Anliegen melden wir uns ggf. erst ' +
        'am nächsten Werktag – bei dringenden Themen auch am Wochenende und an ' +
        'Feiertagen. Tipp: Die KI hilft dir in den meisten Fällen sofort weiter.',
      onSubmit: () => {
        // Unassigning moves the conversation into the cockpit's "unassigned"
        // alert bucket, and the message is what makes the handoff visible there.
        // The update has to land first, or the AI answers it.
        setBusy(true);
        const content: SupportMessageBlock[] = [
          { type: 'text', text: 'Ich möchte mit einem Menschen sprechen.' },
        ];
        updateConversation(conversationId, {
          stopAIFromReplying: true,
          assignedTo: null,
        })
          .then(() =>
            // Roll back when only the message fails: otherwise the AI is stopped
            // with nothing in the cockpit to show for it, and the button that
            // would let the user retry is already gone.
            postMessage(conversationId, { content }).catch(() =>
              updateConversation(conversationId, {
                stopAIFromReplying: false,
                assignedTo: 'ai',
              }),
            ),
          )
          .catch(() => undefined)
          .finally(() => setBusy(false));
      },
    });
  };

  const handleNotifySave = (email: string) => {
    if (!activeId) return Promise.resolve();
    return updateConversation(activeId, {
      notifyByEmail: true,
      notifyEmail: email,
    });
  };

  const activeConversation = activeId
    ? conversations.find((c) => c.id === activeId) || null
    : null;

  // Server-side read mark (readAt.{uid} via POST /read): only while the open
  // chat is actually unread AND visible — a hidden tab must not clear the
  // unread state; coming back (focus or visibilitychange — iOS fires no focus
  // event on app-switcher return) marks it then. The ref dedups per
  // (conversation, lastMessage) and only advances on success, so the snapshot
  // round-trip fires no duplicate POSTs while a failed call retries on the
  // next user-driven focus/visibility event instead of looping.
  const activeUnread =
    !!activeConversation &&
    isConversationUnread(activeConversation, uid || undefined);
  const lastMessageAt = activeConversation?.lastMessage?.createdAt ?? 0;
  // Stale pairing during a restaurant switch (old conversation, new context)
  // would 403 on the API — skip until the list has caught up. Primitive deps
  // only: the conversation object gets a new identity on every snapshot.
  const activeMatchesRestaurant =
    !!activeConversation && activeConversation.restaurantId === restaurantId;
  const readMarkedRef = useRef<{ uid: string; id: string; at: number }>({
    uid: '',
    id: '',
    at: 0,
  });
  const readMarkInFlightRef = useRef(false);
  useEffect(() => {
    if (!activeId || !activeUnread || !activeMatchesRestaurant || !uid) return;
    const conversationId = activeId;
    const mark = () => {
      if (document.visibilityState === 'hidden') return;
      if (readMarkInFlightRef.current) return;
      const prev = readMarkedRef.current;
      if (
        prev.uid === uid &&
        prev.id === conversationId &&
        lastMessageAt <= prev.at
      )
        return;
      readMarkInFlightRef.current = true;
      markConversationRead(conversationId)
        .then(() => {
          readMarkedRef.current = { uid, id: conversationId, at: lastMessageAt };
        })
        .catch(() => undefined)
        .finally(() => {
          readMarkInFlightRef.current = false;
        });
    };
    mark();
    window.addEventListener('focus', mark);
    document.addEventListener('visibilitychange', mark);
    return () => {
      window.removeEventListener('focus', mark);
      document.removeEventListener('visibilitychange', mark);
    };
  }, [
    activeId,
    activeUnread,
    activeMatchesRestaurant,
    lastMessageAt,
    uid,
    markConversationRead,
  ]);

  const userEmail = user?.data?.email || '';
  const defaultNotifyEmail =
    userEmail && !userEmail.toLowerCase().startsWith('tablet')
      ? userEmail
      : restaurantEmail;

  // The topic asked for could not be loaded: offer to load it again instead
  // of silently showing the overview.
  const topicLoadFailed = templatesFailed && topicPending;
  const inTicket = !!activeTicket;
  const inChat = (!!activeId || !!askTicket || !!pendingTemplate) && !inTicket;
  const inChangelog = changelogOpen && !inTicket && !inChat;

  // All views share one scroll container: a ticket opened from the end of a
  // chat would start scrolled down.
  useLayoutEffect(() => {
    if (activeTicketId && bodyRef.current) bodyRef.current.scrollTop = 0;
  }, [activeTicketId]);
  // The chat underneath the ticket if it is about it, else the latest one
  // (conversations come newest first).
  const aboutTicket = (c: { tickets?: string[] }) =>
    !!activeTicket && (c.tickets || []).includes(activeTicket.id);
  const ticketConversation =
    conversations.find((c) => c.id === activeId && aboutTicket(c)) ||
    conversations.find(aboutTicket);

  return (
    <aside
      ref={panelRef}
      className={`support-panel${
        variant === 'page' ? ' support-panel--page' : ''
      }`}
      aria-label="Support"
    >
      <header className="support-panel__header">
        <div className="support-panel__header-left">
          {(inChat || inTicket || inChangelog) && (
            <IconButton
              size="small"
              aria-label="Zurück"
              // Leaving mid-send drops the template or ticket a retry needs.
              disabled={busy && inChat && !activeId}
              onClick={back}
            >
              <ChevronLeft color="inherit" />
            </IconButton>
          )}
          <Typography variant="h5">
            {inTicket ? 'Ticket' : inChangelog ? 'Neuigkeiten' : 'Support'}
          </Typography>
        </div>
        <div className="support-panel__header-actions">
          {inChat && !!activeId && (
            <button
              type="button"
              className="support-done"
              disabled={busy}
              onClick={handleClose}
            >
              <CheckCircleOutline fontSize="inherit" />
              Erledigt
            </button>
          )}
          <IconButton
            size="small"
            aria-label="Support schließen"
            onClick={() => {
              // Always clear the persisted open flag; the page variant also runs
              // its onClose (navigation) so the desktop panel can't resurface.
              setSupportOpen(false);
              onClose?.();
            }}
          >
            <Close color="inherit" />
          </IconButton>
        </div>
      </header>

      <div className="support-panel__body" ref={bodyRef}>
        {topicLoadFailed ? (
          <div className="support-topic-retry" role="alert">
            <p>
              Das Thema konnte nicht geladen werden. Bitte versuche es erneut.
            </p>
            <button type="button" onClick={retryTemplates}>
              Erneut versuchen
            </button>
          </div>
        ) : inChangelog ? (
          <ChangelogView />
        ) : inTicket ? (
          <TicketDetail
            ticket={activeTicket as RestaurantTicketSummary}
            conversation={ticketConversation}
            conversationLoading={conversationsLoading}
            onOpenConversation={() => {
              if (!ticketConversation) return;
              // Already the chat underneath this ticket: just uncover it.
              if (ticketConversation.id === activeId) setActiveTicketId(null);
              else openConversation(ticketConversation.id, activeTicketId);
            }}
            onAsk={() => {
              setAskSnapshot(activeTicket);
              setActiveTicketId(null);
              setReturnTicketId(null);
              setActiveId(null);
            }}
          />
        ) : inChat ? (
          <ChatView
            conversationId={activeId}
            currentUid={uid || ''}
            tickets={tickets}
            pendingTicket={
              askTicket || tickets.find((t) => t.id === returnTicketId)
            }
            pendingIntro={resolveTr(language, pendingTemplate?.intro)}
            onSelectOption={
              topicPending
                ? undefined
                : (value) => void handleSubmit(value).catch(() => undefined)
            }
            onRate={handleRate}
            onOpenTicket={openTicket}
            onConnectHuman={handleConnectHuman}
            connectDisabled={busy || topicPending}
          />
        ) : (
          <SupportOverview
            conversations={conversations}
            tickets={tickets}
            templates={templates}
            templatesDisabled={busy}
            currentUid={uid || ''}
            ticketsExpanded={ticketsExpanded}
            conversationsExpanded={conversationsExpanded}
            onToggleTickets={() => setTicketsExpanded(!ticketsExpanded)}
            onToggleConversations={() =>
              setConversationsExpanded(!conversationsExpanded)
            }
            onStartTemplate={startTemplate}
            onOpenConversation={openConversation}
            onOpenTicket={openTicket}
            onOpenChangelog={() => {
              navigatedRef.current = true;
              setChangelogOpen(true);
            }}
          />
        )}
      </div>

      {inChat && activeConversation?.stopAIFromReplying && (
        <NotifyByEmail
          conversation={activeConversation}
          defaultEmail={defaultNotifyEmail}
          onSave={handleNotifySave}
        />
      )}

      {!inTicket && !inChangelog && (
        <SupportComposer
          disabled={busy || topicPending}
          autoFocus={inChat && !activeId}
          placeholder={
            topicPending
              ? 'Thema wird geladen …'
              : !inChat || pendingTemplate
                ? 'Schreib uns eine Nachricht …'
                : activeId
                  ? 'Antwort schreiben …'
                  : 'Frage zu diesem Ticket schreiben …'
          }
          onSubmit={handleSubmit}
          onScreenshot={variant === 'page' ? undefined : captureScreenshot}
        />
      )}
    </aside>
  );
};

export default SupportPanel;
